import { metricsBus } from "@/common/buses";
import { useDeviceType } from "@/common/hooks";
import { useGetSearches } from "@/common/hooks/useGetSearches";
import { apiSearch, apiCreators, apiNiches } from "@/common/services";
import {
  ICreatorsItems,
  INichesItems,
  IPaginatedNicheList,
  ITagsList,
  IUserSearchResults
} from "@/common/api/services/types";
import { deviceTypeEnum } from "@/common/utils/device";
import { useCallback, useEffect, useMemo, useState } from "react";

interface IUseSearchResults {
  searchValue: string;
}

const MAX_DESKTOP_ITEMS = 5;
const MAX_MOBILE_ITEMS = 3;
const DELAY = 500;

export const useSearchResults = ({ searchValue }: IUseSearchResults) => {
  const [debouncedValue, setDebouncedValue] = useState<string>("");
  const deviceType = useDeviceType();
  const isPhone = useMemo(() => deviceType === deviceTypeEnum.PHONE, [deviceType]);

  const { isLoading: creatorsLoading, data: creatorList } = useGetSearches<IUserSearchResults>(
    "creators-searches",
    debouncedValue,
    useCallback((query: string) => apiCreators.searchCreators({ query, order: "best_match" }), [])
  );

  const { data: nicheList, isLoading: nichesLoading } = useGetSearches<IPaginatedNicheList>(
    "niches-searches",
    debouncedValue,
    useCallback((query: string) => apiNiches.searchNiches({ query, order: "best_match" }), [])
  );

  const { data: tags, isLoading: tagsLoading } = useGetSearches<ITagsList[]>(
    "tags-searches",
    debouncedValue,
    useCallback((query: string) => apiSearch.getTagsSearches(query), []),
  );

  const hasSearchData = useMemo(() => nicheList && creatorList && tags, [nicheList, creatorList, tags]);

  const searchResults = useMemo(() => {
    const itemsLimit = isPhone ? MAX_MOBILE_ITEMS : MAX_DESKTOP_ITEMS;

    return {
      creators: (creatorList?.items?.slice(0, itemsLimit) ?? []).map(creator => {
        return {
          type: "creator",
          text: creator.username,
          image: creator.profileImageUrl,
          verified: creator.verified,
          name: creator.name ?? creator.username,
          userId: creator.username,
          studio: creator.studio ?? false,
          followers: creator.followers,
        } as ICreatorsItems;
      }),
      niches: (nicheList?.niches.slice(0, itemsLimit) ?? []).map(niche => {
        return {
          type: "niche",
          text: niche.name,
          id: niche.id,
          image: niche.thumbnail,
          subscribers: niche.subscribers,
        } as INichesItems;
      }),
      tags: tags?.slice(0, itemsLimit) ?? [],
    };
  }, [creatorList, nicheList, tags, isPhone]);

  const totalCount = useMemo(() => {
    return (creatorList?.items?.length || 0) + (nicheList?.niches.length || 0) + (tags?.length || 0);
  }, [creatorList, nicheList, tags]);

  const sendSearchGaEvent = useCallback((query: string, totalCount: number) => {
    metricsBus.emit("searched", { query, totalCount });
  }, []);

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      setDebouncedValue(searchValue);
    }, DELAY);
    return () => clearTimeout(delayInputTimeoutId);
  }, [searchValue]);

  useEffect(() => {
    if (hasSearchData) {
      sendSearchGaEvent(searchValue, totalCount);
    }
  }, [hasSearchData]);

  return {
    ...searchResults,
    creatorsLoading: creatorsLoading || creatorList === undefined,
    nichesLoading: nichesLoading || nicheList === undefined,
    tagsLoading: tagsLoading || tags === undefined,
  };
};
